// Wspólne klocki czatów (panel kierowcy i panel admina): pełnoekranowa powłoka, okno wiadomości,
// pole pisania bez przerysowywania całej strony przy każdej literze + pasek polskich znaków.
import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { createPortal } from "react-dom";

// ====================== Reakcje na wiadomości (jak w Messengerze) ======================
// Jedna reakcja na osobę (wybór innej ją podmienia). Dane trzymane w wiadomości: reactions = { "❤️": ["Imię", ...] }.
export const REACTION_SET = ["👍", "❤️", "😂", "😮", "😢", "😡"];
export function withReaction(msg, emoji, user) {
  const old = msg.reactions || {};
  const reactions = {};
  for (const [e, users] of Object.entries(old)) {
    const rest = (users || []).filter((u) => u !== user);
    if (rest.length) reactions[e] = rest;
  }
  const had = (old[emoji] || []).includes(user);
  if (!had) reactions[emoji] = [...(reactions[emoji] || []), user];
  return { ...msg, reactions };
}

// ====================== Czat 3.0 — powłoka pełnoekranowa ======================
// Czat otwiera się na CAŁY ekran (więcej miejsca na wiadomości), dopasowuje się do klawiatury
// (visualViewport) i blokuje przewijanie strony pod spodem. Renderowany do .app-root (portal),
// żeby motyw jasny/ciemny działał tak samo jak w reszcie aplikacji.
// Dodatki: przesunięcie wiadomości w bok = odpowiedź (gest), wskaźnik braku internetu, wibracja przy wysyłaniu,
// w panelu admina: dotknięcie wiadomości pokazuje jej akcje (odpowiedz / edytuj / przypnij / usuń),
// reakcje: przytrzymanie (lub prawy przycisk myszy) = pasek reakcji + menu, podwójne dotknięcie = serduszko,
// dotknięcie odznaki reakcji = lista, kto zareagował.
const REPLY_BTN = '.msg-reply-btn, .group-reply-link, button[aria-label="Odpowiedz"]';
const INTERACTIVE = "button, a, audio, input, textarea, .voice-wave, .voice-player, .emoji-picker, .msg-reaction-pill, .chat-edit-row";
const esc = (s) => (window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/"/g, '\\"'));
export function ChatShell({ kind, children, reactApi }) {
  const ref = useRef(null);
  const apiRef = useRef(reactApi);
  apiRef.current = reactApi;
  const barOpenRef = useRef(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  const [hint, setHint] = useState(false);
  const [bar, setBar] = useState(null); // { mid, mine, top, bottom, left, right, rootW, rootH }
  const [sheet, setSheet] = useState(null); // id wiadomości, której reakcje pokazujemy
  const [burst, setBurst] = useState(null); // { k, x, y, emoji }
  const [toast, setToast] = useState("");
  const [hov, setHov] = useState(-1); // emotka pod palcem przy przesuwaniu po pasku
  const hovRef = useRef(-1);
  const pickRef = useRef(null);
  const popCtx = useRef(null);
  barOpenRef.current = !!bar;
  // krótki, cichy "pyk" przy reakcji (jak w komunikatorach)
  const pop = (freq) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const c = popCtx.current || (popCtx.current = new Ctx());
      if (c.state === "suspended") c.resume();
      const o = c.createOscillator();
      const g = c.createGain();
      const t = c.currentTime;
      o.type = "sine";
      o.frequency.setValueAtTime(freq || 700, t);
      o.frequency.exponentialRampToValueAtTime((freq || 700) * 1.6, t + 0.09);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.07, t + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
      o.connect(g);
      g.connect(c.destination);
      o.start(t);
      o.stop(t + 0.16);
    } catch (e) {
      /* ignore */
    }
  };
  const popRef = useRef(pop);
  popRef.current = pop;
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("cx-lock");
    const vv = window.visualViewport;
    // Wysokość z visualViewport ustawiamy tylko, gdy pisze się w polu (jest klawiatura). Gdy klawiatury nie ma,
    // czat zawsze zajmuje CAŁY ekran (CSS: top/bottom 0) — na iPhonie zdarzenie "resize" po schowaniu klawiatury
    // bywa gubione i czat zostawał ucięty w połowie.
    const editing = () => {
      const a = document.activeElement;
      return !!a && (a.tagName === "TEXTAREA" || (a.tagName === "INPUT" && a.type !== "file" && a.type !== "checkbox"));
    };
    const fit = () => {
      const el = ref.current;
      if (!el) return;
      if (!vv || vv.scale > 1.02 || !editing()) {
        if (el.style.height) el.style.height = "";
        if (el.style.transform) el.style.transform = "";
        return;
      }
      el.style.height = `${Math.round(vv.height)}px`;
      el.style.transform = vv.offsetTop > 0 ? `translateY(${Math.round(vv.offsetTop)}px)` : "";
    };
    const later = () => setTimeout(fit, 160);
    fit();
    if (vv) {
      vv.addEventListener("resize", fit);
      vv.addEventListener("scroll", fit);
    }
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", later);
    document.addEventListener("focusin", fit);
    document.addEventListener("focusout", later);
    document.addEventListener("visibilitychange", fit);
    const safety = setInterval(fit, 700);
    return () => {
      root.classList.remove("cx-lock");
      clearInterval(safety);
      if (vv) {
        vv.removeEventListener("resize", fit);
        vv.removeEventListener("scroll", fit);
      }
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", later);
      document.removeEventListener("focusin", fit);
      document.removeEventListener("focusout", later);
      document.removeEventListener("visibilitychange", fit);
    };
  }, []);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  // jednorazowa podpowiedź o gestach (tylko ekrany dotykowe)
  useEffect(() => {
    let seen = true;
    try {
      seen = !!localStorage.getItem("patex-react-hint");
    } catch (e) {
      /* ignore */
    }
    if (seen || !(window.matchMedia && window.matchMedia("(hover: none)").matches)) return undefined;
    const t1 = setTimeout(() => setHint(true), 1400);
    const t2 = setTimeout(() => {
      setHint(false);
      try {
        localStorage.setItem("patex-react-hint", "1");
        localStorage.setItem("patex-swipe-hint", "1");
      } catch (e) {
        /* ignore */
      }
    }, 7000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 1500);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    if (!burst) return undefined;
    const t = setTimeout(() => setBurst(null), 1100);
    return () => clearTimeout(t);
  }, [burst]);
  // wyróżnienie wiadomości, której dotyczy pasek reakcji (wynosimy ją nad przyciemnienie)
  useEffect(() => {
    if (!bar || !ref.current) return undefined;
    const row = ref.current.querySelector(`[data-mid="${esc(bar.mid)}"]`);
    if (row) row.classList.add("cx-react-target");
    const el = ref.current;
    const close = () => setBar(null);
    el.addEventListener("scroll", close, true);
    return () => {
      if (row) row.classList.remove("cx-react-target");
      el.removeEventListener("scroll", close, true);
    };
  }, [bar && bar.mid]);
  // gesty: przesunięcie = odpowiedź, długie przytrzymanie = reakcje, podwójne dotknięcie = serduszko
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const admin = String(kind).startsWith("admin");
    let tgt = null;
    let dir = 1;
    let sx = 0;
    let sy = 0;
    let dx = 0;
    let locked = false;
    let fired = false;
    let moved = false;
    let lp = 0;
    let lpFired = false;
    let suppressUntil = 0;
    let lastTap = { mid: null, t: 0 };
    let lastHeart = 0;
    const clearLp = () => {
      clearTimeout(lp);
      lp = 0;
    };
    const msgAt = (t) => {
      if (!t || !t.closest) return null;
      const bub = t.closest(".chat-bubble");
      const row = bub && bub.closest("[data-mid]");
      if (!bub || !row || !root.contains(row)) return null;
      return { bub, row, mid: row.getAttribute("data-mid"), mine: row.classList.contains("mine") };
    };
    const rel = (m) => {
      const rr = root.getBoundingClientRect();
      const br = m.bub.getBoundingClientRect();
      return { rr, br };
    };
    const buzz = (n) => {
      try {
        navigator.vibrate && navigator.vibrate(n);
      } catch (e2) {
        /* ignore */
      }
    };
    const openBar = (m) => {
      if (!apiRef.current) return;
      const { rr, br } = rel(m);
      setSheet(null);
      setBar({ mid: m.mid, mine: m.mine, top: br.top - rr.top, bottom: br.bottom - rr.top, left: br.left - rr.left, right: br.right - rr.left, rootW: rr.width, rootH: rr.height });
      buzz(14);
    };
    const heart = (m) => {
      const api = apiRef.current;
      if (!api || Date.now() - lastHeart < 700) return;
      lastHeart = Date.now();
      const msg = api.get(m.mid);
      const already = msg && msg.reactions && (msg.reactions["❤️"] || []).includes(api.me);
      if (!already) api.toggle(m.mid, "❤️");
      const { rr, br } = rel(m);
      setBurst({ k: Date.now(), x: br.left - rr.left + br.width / 2, y: br.top - rr.top + br.height / 2, emoji: "❤️" });
      popRef.current(620);
      buzz(12);
    };
    const reset = (animate) => {
      const t = tgt;
      tgt = null;
      if (!t) return;
      t.style.transition = animate ? "transform .24s cubic-bezier(.22,1,.36,1)" : "";
      t.style.transform = "";
      setTimeout(() => {
        t.style.transition = "";
        t.classList.remove("cx-swiping");
      }, 280);
    };
    const onStart = (e) => {
      moved = false;
      lpFired = false;
      clearLp();
      const t = e.target.closest && e.target.closest(".msg-with-reply, .msg-with-delete, .chat-bubble-col");
      if (!t || !root.contains(t) || !e.touches || e.touches.length !== 1) {
        tgt = null;
        return;
      }
      if (e.target.closest(INTERACTIVE)) {
        tgt = null;
        return;
      }
      tgt = t;
      dir = t.classList.contains("mine") || (t.parentElement && t.parentElement.classList.contains("mine")) ? -1 : 1;
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
      dx = 0;
      locked = false;
      fired = false;
      const m = msgAt(e.target);
      if (m && apiRef.current) {
        lp = setTimeout(() => {
          lpFired = true;
          reset(false);
          openBar(m);
        }, 400);
      }
    };
    const onMove = (e) => {
      if (lpFired && barOpenRef.current) {
        const t0 = e.touches[0];
        const el = document.elementFromPoint(t0.clientX, t0.clientY);
        const emo = el && el.closest ? el.closest(".cx-react-emo") : null;
        const idx = emo ? parseInt(emo.getAttribute("data-i"), 10) : -1;
        if (idx !== hovRef.current) {
          hovRef.current = idx;
          setHov(idx);
          if (idx >= 0) buzz(6);
        }
        return;
      }
      if (lp) {
        const mx = Math.abs(e.touches[0].clientX - sx);
        const my = Math.abs(e.touches[0].clientY - sy);
        if (mx > 8 || my > 8) {
          clearLp();
          moved = true;
        }
      }
      if (!tgt) return;
      const x = (e.touches[0].clientX - sx) * dir;
      const y = e.touches[0].clientY - sy;
      if (!locked) {
        if (Math.abs(y) > 10 && Math.abs(y) > Math.abs(x)) {
          tgt = null;
          return;
        }
        if (x > 10 && x > Math.abs(y)) {
          locked = true;
          tgt.classList.add("cx-swiping");
        } else return;
      }
      dx = Math.max(0, Math.min(x, 110));
      tgt.style.transform = `translateX(${dx * 0.8 * dir}px)`;
      tgt.style.setProperty("--sw", String(Math.min(1, dx / 70)));
      if (dx >= 70 && !fired) {
        fired = true;
        buzz(12);
      }
      if (dx < 70) fired = false;
    };
    const onEnd = (e) => {
      clearLp();
      if (lpFired) {
        lpFired = false;
        suppressUntil = Date.now() + 500;
        // puszczenie palca nad emotką = wybór tej reakcji
        const idx = hovRef.current;
        hovRef.current = -1;
        setHov(-1);
        if (idx >= 0 && pickRef.current) pickRef.current(REACTION_SET[idx]);
        return;
      }
      const wasLocked = locked;
      if (tgt) {
        if (locked && dx >= 70) {
          const btn = tgt.querySelector(REPLY_BTN) || (tgt.parentElement && tgt.parentElement.querySelector(REPLY_BTN));
          if (btn) btn.click();
        }
        reset(true);
      }
      // podwójne dotknięcie = serduszko
      if (!moved && !wasLocked && apiRef.current && !barOpenRef.current) {
        const m = msgAt(e.target);
        if (m && !e.target.closest(INTERACTIVE)) {
          const now = Date.now();
          if (lastTap.mid === m.mid && now - lastTap.t < 320) {
            lastTap = { mid: null, t: 0 };
            suppressUntil = now + 450;
            heart(m);
          } else lastTap = { mid: m.mid, t: now };
        }
      }
    };
    const onCancel = () => {
      clearLp();
      reset(true);
    };
    const onClick = (e) => {
      const t = e.target;
      if (!t.closest) return;
      if (Date.now() < suppressUntil && t.closest(".chat-bubble")) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      // dotknięcie odznaki reakcji = kto zareagował
      const pill = t.closest(".msg-reaction-pill");
      if (pill && apiRef.current) {
        e.stopPropagation();
        e.preventDefault();
        const row = pill.closest("[data-mid]");
        if (row) {
          setBar(null);
          setSheet(row.getAttribute("data-mid"));
        }
        return;
      }
      if (t.closest(".chat-send-btn")) buzz(10);
      // panel admina: dotknięcie dymka pokazuje/chowa akcje wiadomości (odpowiedz, edytuj, przypnij, usuń)
      if (admin) {
        const bub = t.closest(".chat-bubble");
        if (bub && !t.closest("a, button, audio, .voice-player, .chat-edit-row")) {
          const row = bub.closest(".msg-with-delete, .chat-bubble-row");
          if (row) {
            const was = row.classList.contains("cx-actions");
            root.querySelectorAll(".cx-actions").forEach((n) => n.classList.remove("cx-actions"));
            if (!was) row.classList.add("cx-actions");
          }
        }
      }
    };
    const onContext = (e) => {
      const m = msgAt(e.target);
      if (!m || !apiRef.current) return;
      e.preventDefault();
      if (!barOpenRef.current) openBar(m);
    };
    const onDbl = (e) => {
      const m = msgAt(e.target);
      if (m && !e.target.closest(INTERACTIVE)) heart(m);
    };
    root.addEventListener("touchstart", onStart, { passive: true });
    root.addEventListener("touchmove", onMove, { passive: true });
    root.addEventListener("touchend", onEnd, { passive: true });
    root.addEventListener("touchcancel", onCancel, { passive: true });
    root.addEventListener("click", onClick, true);
    root.addEventListener("contextmenu", onContext);
    root.addEventListener("dblclick", onDbl);
    return () => {
      clearLp();
      root.removeEventListener("touchstart", onStart);
      root.removeEventListener("touchmove", onMove);
      root.removeEventListener("touchend", onEnd);
      root.removeEventListener("touchcancel", onCancel);
      root.removeEventListener("click", onClick, true);
      root.removeEventListener("contextmenu", onContext);
      root.removeEventListener("dblclick", onDbl);
    };
  }, [kind]);

  const api = reactApi;
  const myReaction = (mid) => {
    const msg = api && api.get(mid);
    if (!msg || !msg.reactions) return null;
    return Object.keys(msg.reactions).find((e) => (msg.reactions[e] || []).includes(api.me)) || null;
  };
  const pickReaction = (emoji) => {
    if (!bar || !api) return;
    api.toggle(bar.mid, emoji);
    setBar(null);
    popRef.current(emoji === "❤️" ? 620 : 760);
    try {
      navigator.vibrate && navigator.vibrate(8);
    } catch (e) {
      /* ignore */
    }
  };
  const barAction = (what) => {
    if (!bar || !ref.current) return;
    const row = ref.current.querySelector(`[data-mid="${esc(bar.mid)}"]`);
    setBar(null);
    if (!row) return;
    if (what === "reply") {
      const btn = row.querySelector(REPLY_BTN);
      if (btn) btn.click();
    } else {
      const bub = row.querySelector(".chat-bubble");
      if (!bub) return;
      const clone = bub.cloneNode(true);
      clone.querySelectorAll(".cx-stamp, .priority-badge, .important-badge, .chat-quote, .chat-bubble-author, .voice-player-wrap").forEach((n) => n.remove());
      const text = (clone.innerText || clone.textContent || "").trim();
      if (!text) {
        setToast("Brak tekstu do skopiowania");
        return;
      }
      const done = () => setToast("Skopiowano");
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, done);
      else {
        try {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        } catch (e) {
          /* ignore */
        }
        done();
      }
    }
  };
  let barPos = null;
  if (bar) {
    const barW = REACTION_SET.length * 46 + 18;
    const popH = 112;
    const above = bar.top - 78 > popH;
    const top = above ? bar.top - popH - 6 : Math.min(bar.bottom + 8, Math.max(80, bar.rootH - popH - 90));
    const left = Math.max(10, Math.min(bar.mine ? bar.right - barW : bar.left, bar.rootW - barW - 10));
    barPos = { top, left, width: barW };
  }
  pickRef.current = pickReaction;
  const label = (u) => (api && u === api.me ? "Ty" : u === "Administrator" ? "Szef" : u);
  const sheetMsg = sheet && api ? api.get(sheet) : null;
  const sheetRows = [];
  if (sheetMsg && sheetMsg.reactions) {
    Object.entries(sheetMsg.reactions).forEach(([emo, users]) => (users || []).forEach((u) => sheetRows.push({ emo, u })));
  }
  const host = document.querySelector(".app-root") || document.body;
  return createPortal(
    <div ref={ref} className={`cx cx-${kind}`}>
      {offline && <div className="cx-offline">Brak połączenia z internetem — wiadomości nie zostaną wysłane</div>}
      {hint && <div className="cx-hint">👆 Przytrzymaj wiadomość i przesuń palec na reakcję · dwa dotknięcia = ❤️</div>}
      {children}
      {bar && (
        <>
          <div className="cx-react-overlay" onClick={() => setBar(null)} onContextMenu={(e) => e.preventDefault()} />
          <div className={`cx-react-pop ${bar.mine ? "mine" : ""}`} style={barPos}>
            <div className="cx-react-bar">
              {REACTION_SET.map((e, i) => (
                <button key={e} data-i={i} className={`cx-react-emo ${myReaction(bar.mid) === e ? "sel" : ""} ${hov === i ? "hov" : ""}`} style={{ "--i": i }} onClick={() => pickReaction(e)} aria-label={`Reakcja ${e}`}>
                  {e}
                </button>
              ))}
            </div>
            <div className="cx-react-actions">
              <button onClick={() => barAction("reply")}>↩ Odpowiedz</button>
              <button onClick={() => barAction("copy")}>⧉ Kopiuj</button>
            </div>
          </div>
        </>
      )}
      {sheet && (
        <>
          <div className="cx-react-overlay" onClick={() => setSheet(null)} />
          <div className="cx-who-sheet">
            <div className="cx-who-grip" />
            <div className="cx-who-title">Reakcje · {sheetRows.length}</div>
            {sheetRows.length === 0 && <div className="cx-who-empty">Brak reakcji</div>}
            {sheetRows.map((r) => (
              <button
                key={r.emo + r.u}
                className="cx-who-row"
                onClick={() => {
                  if (api && r.u === api.me) {
                    api.toggle(sheet, r.emo);
                    setSheet(null);
                  }
                }}
              >
                <span className="cx-who-emo">{r.emo}</span>
                <span className="cx-who-name">{label(r.u)}</span>
                {api && r.u === api.me && <small>Dotknij, aby usunąć</small>}
              </button>
            ))}
          </div>
        </>
      )}
      {burst && (
        <div className="cx-burst" style={{ left: burst.x, top: burst.y }} key={burst.k} aria-hidden="true">
          <span className="cx-burst-main">{burst.emoji}</span>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <i key={i} style={{ "--n": i }}>
              {burst.emoji}
            </i>
          ))}
        </div>
      )}
      {toast && <div className="cx-toast">{toast}</div>}
    </div>,
    host
  );
}

// Okno wiadomości: czat pokazuje tylko OSTATNIE ~80 wiadomości (otwiera się dużo szybciej na słabszych telefonach),
// starsze doczytuje przyciskiem "Wcześniejsze wiadomości". Pozycja przewijania zostaje zachowana po doczytaniu.
export const CHAT_WINDOW = 80;
export function useMsgWindow(key, total, scrollRef) {
  const st = useRef({ key: null, start: 0 });
  const anchor = useRef(null);
  const [, bump] = useState(0);
  if (st.current.key !== key) st.current = { key, start: Math.max(0, total - CHAT_WINDOW) };
  React.useLayoutEffect(() => {
    const a = anchor.current;
    const el = scrollRef && scrollRef.current;
    if (!a || !el) return;
    anchor.current = null;
    el.scrollTop = a.t + (el.scrollHeight - a.h);
    setTimeout(() => el.removeAttribute("data-older"), 700);
  });
  const more = () => {
    const el = scrollRef && scrollRef.current;
    if (el) {
      anchor.current = { h: el.scrollHeight, t: el.scrollTop };
      el.setAttribute("data-older", "1");
    }
    st.current.start = Math.max(0, st.current.start - 100);
    bump((n) => n + 1);
  };
  return { start: Math.min(st.current.start, total), more };
}

// ====================== Pole pisania ======================
// Tekst trzymany jest W POLU (nie w stanie wielkiego ekranu) — dzięki temu pisanie nie przerysowuje za każdym znakiem
// całego panelu/czatu (na telefonie to był główny powód "zawieszania"). Rodzic odczytuje tekst przez ref
// (fieldVal / fieldSet / fieldInsert). Na ekranach dotykowych nad polem pojawia się pasek polskich znaków.
const PL = ["ą", "ć", "ę", "ł", "ń", "ó", "ś", "ź", "ż"];
export const fieldVal = (r) => (r && r.current ? r.current.getValue() : "");
export const fieldSet = (r, v) => r && r.current && r.current.setValue(v);
export const fieldInsert = (r, t) => r && r.current && r.current.insert(t);
export const ComposerField = forwardRef(function ComposerField({ className = "chat-input", placeholder, disabled, onEnter, onPaste, onEmptyChange }, ref) {
  const el = useRef(null);
  const emptyRef = useRef(true);
  const [focus, setFocus] = useState(false);
  const [caps, setCaps] = useState(true);
  const [shift, setShift] = useState(false);
  const blurTimer = useRef(0);
  const coarse = typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
  const sync = () => {
    const i = el.current;
    if (!i) return;
    const v = i.value;
    const empty = v.length === 0;
    if (empty !== emptyRef.current) {
      emptyRef.current = empty;
      if (onEmptyChange) onEmptyChange(empty);
    }
    // wielka litera na początku wypowiedzi i po końcu zdania
    const pos = typeof i.selectionStart === "number" ? i.selectionStart : v.length;
    const before = v.slice(0, pos);
    const want = before.trim().length === 0 || /[.!?]\s+$/.test(before);
    setCaps((c) => (c === want ? c : want));
  };
  const insert = (t) => {
    const i = el.current;
    if (!i) return;
    const a = typeof i.selectionStart === "number" ? i.selectionStart : i.value.length;
    const b = typeof i.selectionEnd === "number" ? i.selectionEnd : a;
    i.setRangeText(t, a, b, "end");
    sync();
  };
  useImperativeHandle(
    ref,
    () => ({
      getValue: () => (el.current ? el.current.value : ""),
      setValue: (v) => {
        if (el.current) {
          el.current.value = v || "";
          sync();
        }
      },
      insert,
      focus: () => el.current && el.current.focus(),
    }),
    // eslint-disable-next-line
    []
  );
  useEffect(() => () => clearTimeout(blurTimer.current), []);
  const upper = shift ? !caps : caps;
  return (
    <>
      <input
        ref={el}
        className={className}
        placeholder={placeholder}
        disabled={disabled}
        lang="pl"
        autoComplete="off"
        autoCorrect="on"
        autoCapitalize="sentences"
        spellCheck={true}
        enterKeyHint="send"
        onInput={sync}
        onKeyUp={sync}
        onClick={sync}
        onFocus={() => {
          clearTimeout(blurTimer.current);
          setFocus(true);
          sync();
        }}
        onBlur={() => {
          blurTimer.current = setTimeout(() => setFocus(false), 220);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !(e.nativeEvent && e.nativeEvent.isComposing)) {
            e.preventDefault();
            if (onEnter) onEnter();
          }
        }}
        onPaste={onPaste}
      />
    </>
  );
});
